<template>
  <div class="sp-expandable-area" :style="style">
    <span class="sp-expandable-area__activator" @click="isExpanded = !isExpanded">
      <span class="sp-expandable-area__activator-title">
        <slot name="title">
          {{ activatorLabel }}
        </slot>
      </span>
      <sp-caret :active="isExpanded"></sp-caret>
    </span>
    <div v-if="isExpanded" class="sp-expandable-area__content">
      <slot />
    </div>
  </div>
</template>

<script setup>
/**
 * The SpExpandableArea component is used to display a section that can be expanded or collapsed.
 *
 * @displayName ExpandableArea
 * @group Custom Elements
 * @component sp-expandable-area
 */
import { computed, ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { props as expandableAreaProps } from "./props.js";

const emit = defineEmits(["update-expanded"]);

const props = defineProps(expandableAreaProps);

const isExpanded = ref(toBoolean(props.expanded));
watch(isExpanded, (value) => emit("update-expanded", value));

watch(
  () => props.expanded,
  (value) => (isExpanded.value = toBoolean(value)),
);

const activatorLabel = computed(() => {
  if (isExpanded.value && props.expandedTitle) {
    return props.expandedTitle;
  }

  return props.title;
});

const style = computed(() => ({
  "--color": props.color,
}));
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-expandable-area {
  --color: var(--sp-comp-expandable-area-activator-color, inherit);
  --hover-color: var(--sp-comp-expandable-area-activator-hover-color, inherit);

  display: flex;
  flex-direction: column;
  width: var(--sp-comp-expandable-area-width, 100%);

  &__activator {
    cursor: pointer;
    color: var(--color);
    display: inline-grid;
    grid-template-columns: var(--sp-comp-expandable-area-activator-columns, max-content auto);
    justify-content: var(--sp-comp-expandable-area-activator-justify-content, inherit);
    align-items: center;
    gap: 0.25ch;
    font-weight: var(--sp-comp-expandable-area-activator-font-weight, 500);
    font-size: var(--sp-comp-expandable-area-activator-font-size, 0.875rem);
    margin-bottom: var(--sp-comp-expandable-area-activator-margin-bottom, 0);
    min-height: var(--sp-comp-expandable-area-activator-min-height, 0);

    &:hover {
      --color: var(--hover-color);
    }
  }

  &__activator-title {
    display: inline-block;
  }

  sp-caret {
    display: flex;
  }
}
</style>
